/**
 * ProfileMenuDropdown — self-serve avatar menu (glass panel).
 * Reuses theme toggle styling from SelfServePortalLayout ThemeSelector.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sun,
  Moon,
  Monitor,
  ChevronRight,
  LogOut,
  CreditCard,
  HelpCircle,
  Languages,
  Coins,
  Building2,
  Loader2,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useI18n } from '../../context/I18nContext';
import { useSupportWidget } from '../../context/SupportWidgetContext';
import { useAuth } from '../../context/AuthContext';
import {
  WORKSPACE_PRODUCTS,
  detectWorkspaceProduct,
  getFundGeniusManagePlanPath,
} from '../../config/productWorkspaceConfig';
import { toast } from 'sonner';
import { formatAccountSwitcherSubtitle } from '../../utils/accountSwitcherLabel';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/** Same control as former top-bar ThemeSelector */
export const PreferencesThemeControl = ({ tokens, theme, onThemeChange }) => {
  const { t } = useI18n();
  const options = [
    { value: 'light', icon: Sun, label: t('settings.theme.light') },
    { value: 'dark', icon: Moon, label: t('settings.theme.dark') },
    { value: 'system', icon: Monitor, label: t('settings.theme.system') },
  ];

  return (
    <div
      className="flex items-center gap-1 p-1 rounded-xl w-full"
      style={{
        background: tokens.isDark ? 'rgba(142,218,182,0.08)' : 'rgba(0,0,0,0.05)',
        border: `1px solid ${tokens.content.borderSubtle}`,
      }}
      data-testid="profile-theme-control"
    >
      {options.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => onThemeChange(value)}
          className={`flex-1 p-2 rounded-lg transition-all duration-200 ${
            theme === value ? 'shadow-sm' : 'opacity-60 hover:opacity-100'
          }`}
          style={{
            background:
              theme === value
                ? tokens.isDark
                  ? 'rgba(7,28,21,0.75)'
                  : 'rgba(255,255,255,0.90)'
                : 'transparent',
            color: tokens.topBar.text,
          }}
          title={label}
          aria-label={label}
        >
          <Icon size={16} className="mx-auto" />
        </button>
      ))}
    </div>
  );
};

const getPlanBadgeLabel = (plan, t) => {
  const normalized = String(plan || 'standard').toLowerCase();
  if (['premium', 'pro', 'professional'].includes(normalized)) return t('profileMenu.planPremium');
  return t('profileMenu.planStandard');
};

const ProfileMenuDropdown = ({
  user,
  tokens,
  onLogout,
  resolvedTheme,
  theme,
  onThemeChange,
  product: productProp,
  managePlanPath,
}) => {
  const navigate = useNavigate();
  const { language, setLanguage, availableLanguages, t } = useI18n();
  const { openSupportWidget } = useSupportWidget();
  const { getAuthHeaders, listAccounts, switchAccount } = useAuth();
  const product = productProp || detectWorkspaceProduct();
  const isFundGenius = product === WORKSPACE_PRODUCTS.FUNDGENIUS_HQ;
  const isAgencyProduct = product === WORKSPACE_PRODUCTS.AGENCY;
  const showPersonalWallet = !isFundGenius && !isAgencyProduct;
  const resolvedManagePlanPath = managePlanPath
    || (isFundGenius
      ? getFundGeniusManagePlanPath()
      : (isAgencyProduct && user?.agency_id
        ? `/agency/${user.agency_id}/settings?section=billing`
        : '/billing'));
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [languageSaving, setLanguageSaving] = useState(false);
  const [walletCredits, setWalletCredits] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);

  const fetchWalletCredits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/delivery/credits/balance`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setWalletCredits(response.data?.available_credits ?? 0);
    } catch {
      setWalletCredits(null);
    }
  }, [getAuthHeaders]);

  const fetchAccounts = useCallback(async () => {
    if (typeof listAccounts !== 'function') return;
    try {
      setAccountsLoading(true);
      const data = await listAccounts();
      setAccounts(Array.isArray(data?.accounts) ? data.accounts : []);
    } catch {
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [listAccounts]);

  useEffect(() => {
    if (menuOpen) {
      if (showPersonalWallet) {
        fetchWalletCredits();
      }
      fetchAccounts();
    }
  }, [menuOpen, fetchWalletCredits, fetchAccounts, showPersonalWallet]);

  const activeAccountId =
    user?.active_account?.account_id
    || (
      user?.account_type === 'self_repair' || user?.portal === 'self_repair'
        ? null
        : user?.agency_id
    )
    || null;

  const currentAccount =
    accounts.find((account) => account.account_id === activeAccountId)
    || user?.active_account
    || null;

  const otherAccounts = accounts.filter(
    (account) => account.account_id && account.account_id !== activeAccountId
  );

  const handleSwitchAccount = async (account) => {
    if (!account?.account_id || switchingId) return;
    setSwitchingId(account.account_id);
    try {
      const result = await switchAccount(account.account_id);
      setMenuOpen(false);
      const redirectTo = result?.redirect_to;
      if (redirectTo && String(redirectTo).startsWith('/')) {
        window.location.assign(redirectTo);
      } else {
        window.location.reload();
      }
    } catch {
      toast.error(t('profileMenu.switchFailed'));
      setSwitchingId(null);
    }
  };

  const panelBg = tokens.isDark ? 'rgba(7,28,21,0.96)' : 'rgba(255,255,255,0.98)';
  const panelBorder = tokens.isDark ? 'rgba(142,218,182,0.14)' : 'rgba(15,172,108,0.12)';
  const primary = tokens.isDark ? '#EAF7F0' : '#111827';
  const muted = tokens.isDark ? '#93AE9F' : '#6B7280';
  const rowHover = tokens.isDark ? 'rgba(142,218,182,0.08)' : 'rgba(15,172,108,0.06)';

  const credits =
    walletCredits ??
    user?.credits ??
    user?.credit_balance ??
    user?.ai_credits ??
    user?.letter_credits ??
    0;

  const planBadge = getPlanBadgeLabel(user?.plan || user?.subscription_tier, t);

  const handleLanguageSelect = async (code) => {
    if (languageSaving) return;
    setLanguageSaving(true);
    try {
      await setLanguage(code);
    } finally {
      setLanguageSaving(false);
      setLanguageOpen(false);
    }
  };

  const activeLanguageLabel =
    availableLanguages.find((lang) => lang.code === language)?.nativeName || language;

  const menuRowStyle = {
    color: primary,
  };

  return (
    <DropdownMenu
      open={menuOpen}
      onOpenChange={(open) => {
        setMenuOpen(open);
        if (!open) setLanguageOpen(false);
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="p-1 rounded-full transition-opacity hover:opacity-90"
          aria-label={t('profileMenu.openMenu')}
          data-testid="profile-menu-trigger"
          data-product={product}
        >
          <Avatar className="h-8 w-8 border" style={{ borderColor: tokens.content.border }}>
            <AvatarImage src={user?.picture} />
            <AvatarFallback
              className="text-xs font-semibold"
              style={{
                background: resolvedTheme === 'dark' ? 'rgba(15,172,108,0.3)' : '#E5F8EE',
                color: '#0FAC6C',
              }}
            >
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[300px] max-w-[calc(100vw-1.5rem)] p-0 overflow-visible border-0 shadow-none"
        style={{
          background: panelBg,
          border: `1px solid ${panelBorder}`,
          borderRadius: '18px',
          backdropFilter: 'blur(20px) saturate(140%)',
          WebkitBackdropFilter: 'blur(20px) saturate(140%)',
          boxShadow: tokens.isDark
            ? '0 16px 48px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)'
            : '0 16px 48px rgba(16,24,40,0.12), inset 0 1px 0 rgba(255,255,255,0.8)',
        }}
        data-testid="profile-menu-dropdown"
        data-product={product}
      >
        {/* User block */}
        <div className="px-4 pt-4 pb-3 border-b" style={{ borderColor: panelBorder }}>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border" style={{ borderColor: tokens.content.border }}>
              <AvatarImage src={user?.picture} />
              <AvatarFallback
                className="text-sm font-semibold"
                style={{
                  background: resolvedTheme === 'dark' ? 'rgba(15,172,108,0.3)' : '#E5F8EE',
                  color: '#0FAC6C',
                }}
              >
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold truncate" style={{ color: primary }}>
                {user?.name || 'User'}
              </p>
              <p className="text-xs truncate" style={{ color: muted }}>
                {user?.email || ''}
              </p>
            </div>
          </div>
        </div>

        <div className="p-3 space-y-3">
          {/* Account switcher */}
          {(currentAccount || accounts.length > 0 || accountsLoading) && (
            <div data-testid="profile-account-switcher">
              <p
                className="text-[11px] font-bold uppercase tracking-wide mb-2 px-0.5"
                style={{ color: muted }}
              >
                {t('profileMenu.currentAccount')}
              </p>
              {currentAccount ? (
                <div
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-2"
                  style={{
                    background: tokens.isDark ? 'rgba(142,218,182,0.08)' : 'rgba(15,172,108,0.06)',
                    border: `1px solid ${panelBorder}`,
                  }}
                  data-testid="profile-current-account"
                >
                  <Building2 size={18} style={{ color: muted }} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate" style={{ color: primary }}>
                      {currentAccount.name || currentAccount.account_id}
                    </p>
                    <p className="text-[11px] truncate" style={{ color: muted }}>
                      {formatAccountSwitcherSubtitle(currentAccount)}
                    </p>
                  </div>
                </div>
              ) : accountsLoading ? (
                <div className="flex items-center gap-2 px-3 py-2 text-xs" style={{ color: muted }}>
                  <Loader2 size={14} className="animate-spin" aria-hidden />
                  {t('profileMenu.switchingAccount')}
                </div>
              ) : null}

              {(otherAccounts.length > 0 || accountsLoading) && (
                <>
                  <p
                    className="text-[11px] font-bold uppercase tracking-wide mb-1.5 px-0.5"
                    style={{ color: muted }}
                  >
                    {t('profileMenu.switchAccount')}
                  </p>
                  <div
                    className="space-y-1 max-h-40 overflow-y-auto"
                    role="listbox"
                    aria-label={t('profileMenu.availableAccounts')}
                    data-testid="profile-available-accounts"
                  >
                    {accountsLoading && otherAccounts.length === 0 ? (
                      <p className="text-xs px-3 py-2" style={{ color: muted }}>
                        {t('profileMenu.switchingAccount')}
                      </p>
                    ) : (
                      otherAccounts.map((account) => {
                        const busy = switchingId === account.account_id;
                        return (
                          <button
                            key={account.account_id}
                            type="button"
                            role="option"
                            disabled={Boolean(switchingId)}
                            onClick={() => handleSwitchAccount(account)}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left text-sm transition-colors disabled:opacity-60"
                            style={menuRowStyle}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = rowHover;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                            data-testid={`profile-switch-account-${account.account_id}`}
                          >
                            <Building2 size={16} style={{ color: muted }} aria-hidden />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">
                                {account.name || account.account_id}
                              </span>
                              <span className="block text-[11px] truncate" style={{ color: muted }}>
                                {formatAccountSwitcherSubtitle(account)}
                              </span>
                            </span>
                            {busy ? (
                              <Loader2 size={14} className="animate-spin" aria-label={t('profileMenu.switchingAccount')} />
                            ) : (
                              <ChevronRight size={14} style={{ color: muted }} aria-hidden />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Credits card — self-repair only; agency accounts use agency billing. */}
          {showPersonalWallet ? (
          <div
            className="rounded-2xl p-3.5 relative overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, #2a1f08 0%, #4a3812 28%, #8b6914 62%, #c9a227 100%)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
            }}
            data-testid="profile-credits-card"
          >
            <div className="flex items-start justify-between gap-2 relative z-10">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-100/80">
                  {t('profileMenu.credits')}
                </p>
                <p className="text-2xl font-black text-white mt-0.5 tabular-nums">{credits}</p>
              </div>
              <Coins size={22} className="text-amber-200/70 flex-shrink-0" />
            </div>
            <button
              type="button"
              onClick={() => navigate('/billing')}
              className="mt-3 w-full py-2 rounded-xl text-sm font-bold transition-transform hover:scale-[1.01] active:scale-[0.99]"
              style={{
                background: 'linear-gradient(180deg, #f5d76e 0%, #d4a735 45%, #b8891f 100%)',
                color: '#1a1204',
                boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              }}
              data-testid="buy-credits-btn"
            >
              {t('profileMenu.buyCredits')}
            </button>
          </div>
          ) : null}

          {/* Preferences */}
          <div>
            <p
              className="text-[11px] font-bold uppercase tracking-wide mb-2 px-0.5"
              style={{ color: muted }}
            >
              {t('settings.sections.preferences')}
            </p>
            <PreferencesThemeControl
              tokens={tokens}
              theme={theme}
              onThemeChange={onThemeChange}
            />
          </div>

          {/* Language */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setLanguageOpen((v) => !v)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors"
              style={menuRowStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = rowHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
              data-testid="profile-language-row"
            >
              <Languages size={18} style={{ color: muted }} />
              <span className="flex-1 text-left">{t('settings.language.title')}</span>
              <span className="text-xs truncate max-w-[88px]" style={{ color: muted }}>
                {activeLanguageLabel}
              </span>
              <ChevronRight size={16} style={{ color: muted }} />
            </button>
            {languageOpen && (
              <div
                className="absolute right-full top-0 mr-2 py-1 rounded-xl z-[60] min-w-[140px]"
                style={{
                  background: panelBg,
                  border: `1px solid ${panelBorder}`,
                  boxShadow: tokens.isDark
                    ? '0 12px 32px rgba(0,0,0,0.4)'
                    : '0 12px 32px rgba(16,24,40,0.12)',
                }}
                data-testid="profile-language-submenu"
              >
                {availableLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    disabled={languageSaving}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className="w-full text-left px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                    style={{
                      color: language === lang.code ? (tokens.isDark ? '#8FE0B7' : '#0B8C58') : primary,
                      background:
                        language === lang.code
                          ? tokens.isDark
                            ? 'rgba(15,172,108,0.14)'
                            : 'rgba(15,172,108,0.08)'
                          : 'transparent',
                    }}
                    data-testid={`profile-language-${lang.code}`}
                  >
                    {lang.nativeName}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Manage Plan */}
          <button
            type="button"
            onClick={() => navigate(resolvedManagePlanPath)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={menuRowStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = rowHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            data-testid="profile-manage-plan-row"
          >
            <CreditCard size={18} style={{ color: muted }} />
            <span className="flex-1 text-left">{t('profileMenu.managePlan')}</span>
            <span
              className="text-[11px] font-bold px-2.5 py-1 rounded-full"
              style={{
                background: tokens.isDark ? 'rgba(142,218,182,0.12)' : 'rgba(15,172,108,0.10)',
                color: tokens.isDark ? '#8FE0B7' : '#0B8C58',
                border: `1px solid ${panelBorder}`,
              }}
            >
              {planBadge}
            </span>
          </button>

          {/* Help & Support */}
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              openSupportWidget({ product });
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={menuRowStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = rowHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            data-testid="profile-help-support-row"
          >
            <HelpCircle size={18} style={{ color: muted }} />
            <span className="flex-1 text-left">{t('profileMenu.helpSupport')}</span>
          </button>

          <div className="h-px" style={{ background: panelBorder }} />

          {/* Sign Out */}
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors"
            style={{ color: '#EF4444' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = tokens.isDark
                ? 'rgba(239,68,68,0.12)'
                : 'rgba(239,68,68,0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            data-testid="profile-sign-out-btn"
          >
            <LogOut size={18} />
            <span>{t('profileMenu.signOut')}</span>
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileMenuDropdown;
